(ns dashboard.events.spaces
  (:require
   [re-frame.core :as re-frame]
   [graphql-query.core :refer [graphql-query]]
   [dashboard.utils :refer [index-of]]
   [dashboard.interceptors :refer [standard-interceptors]]
   [ajax.core :as ajax]))

(def space-fields
  [:id :name :nameJp :organizationId :isActive :timezone :externalId
   :workDaysByMonth
   [:shiftPatterns [:id :symbol :description :startTime :endTime :isDayOff
                    :hours :wimsFormat]]
   [:shiftTags [:id :symbol :description :color]]
   [:shiftFormulas [:id :color :symbol :patternIds :userIds :teamIds]]
   [:teams [:id :name :nameJp :color :memberIds]]])

;; Get Space

(defn get-space-query
  [{:keys [id]}]
  {:operation {:operation/type :query
               :operation/name :getSpace}
   :queries [[:getSpace
              {:id id}
              space-fields]]})

(re-frame/reg-event-fx
 ::get-space
 standard-interceptors
 (fn [{:keys [db]} [space-id]]
   {:graphql-request {:query (graphql-query (get-space-query {:id space-id}))
                      :on-success [::get-space-success]}}))

(re-frame/reg-event-fx
 ::get-space-success
 standard-interceptors
 (fn [{:keys [db]} [res]]
   (js/console.log res)))

;; List Spaces

(defn list-spaces-by-organization-query
  [{:keys [organization-id]}]
  {:operation {:operation/type :query
               :operation/name :allSpacesByOrganization}
   :queries [[:allSpacesByOrganization
              {:organizationId organization-id}
              {:spaces space-fields}]]})

(re-frame/reg-event-fx
 ::list-spaces-by-organization
 standard-interceptors
 (fn [{:keys [db]} [{:keys [organization-id]}]]
   (let [organization-id (or organization-id
                             (get-in db [:settings :organization :id]))]
     {:graphql-request {:query (graphql-query (list-spaces-by-organization-query
                                               {:organization-id organization-id}))
                        :variables {}
                        :on-success [::list-spaces-by-organization-success]}})))

(re-frame/reg-event-fx
 ::list-spaces-by-organization-success
 standard-interceptors
 (fn [{:keys [db]} [res]]
   {:db (assoc db :spaces (get-in res [:data :allSpacesByOrganization :spaces]))}))

;; Create Space

(defn create-space-query
  [{:keys [space]}]
  {:operation {:operation/type :mutation
               :operation/name :addSpace}
   :queries [[:addSpace
              space
              space-fields]]})

(re-frame/reg-event-fx
 ::create-space
 standard-interceptors
 (fn [{:keys [db]} [{:keys [space]}]]
   {:graphql-request {:query (graphql-query (create-space-query {:space space}))
                      :on-success [::create-space-success]}}))

(re-frame/reg-event-fx
 ::create-space-success
 standard-interceptors
 (fn [{:keys [db]} [res]]
   (if (some? (:errors res))
     {:show-notification!
      {:title "Error Creating Space"
       :intent :intent/danger
       :duration 10000
       :message (clojure.string/join "\n" (map :message (:errors res)))}}
     {:show-notification!
      {:title "Space Created"
       :intent :intent/success
       :duration 10000
       :message (str "You successfuly created " (:name (get-in res [:data :addSpace])))}
      :dispatch [::add-space (get-in res [:data :addSpace])]})))

;; Update Space

(defn update-space-query
  [{:keys [space]}]
  {:operation {:operation/type :mutation
               :operation/name :updateSpace}
   :queries [[:updateSpace
              space
              space-fields]]})

(re-frame/reg-event-fx
 ::update-space
 standard-interceptors
 (fn [{:keys [db]} [{:keys [space]}]]
   {:graphql-request {:query (graphql-query (update-space-query {:space space}))
                      :on-success [::update-space-success]}}))

(re-frame/reg-event-fx
 ::update-space-success
 standard-interceptors
 (fn [{:keys [db]} [res]]
   (if (some? (:errors res))
     {:show-notification!
      {:title "Error Updating Space"
       :intent :intent/danger
       :duration 10000
       :message (clojure.string/join "\n" (map :message (:errors res)))}}
     {:show-notification!
      {:title "Space Updated"
       :intent :intent/success
       :duration 10000
       :message (str "You successfuly update " (:name (get-in res [:data :updateSpace])))}
      :dispatch [::edit-space (get-in res [:data :updateSpace])]})))

;; Delete Space

(defn delete-space-query
  [{:keys [space]}]
  {:operation {:operation/type :mutation
               :operation/name :deleteSpace}
   :queries [[:deleteSpace
              {:id (:id space)}
              space-fields]]})

(re-frame/reg-event-fx
 ::delete-space
 standard-interceptors
 (fn [{:keys [db]} [{:keys [space]}]]
   {:graphql-request {:query (graphql-query (delete-space-query {:space space}))
                      :on-success [::delete-space-success]}}))

(re-frame/reg-event-fx
 ::delete-space-success
 standard-interceptors
 (fn [{:keys [db]} [res]]
   {:dispatch [::remove-space (get-in res [:data :deleteSpace])]}))

;; Add a space to the list of spaces in the application db
(re-frame/reg-event-db
 ::add-space
 standard-interceptors
 (fn [db [space]]
   (update-in db [:spaces] conj space)))

;; Edit a space to the list of spaces in the application db
(re-frame/reg-event-db
 ::edit-space
 standard-interceptors
 (fn [db [space]]
   (let [space-idx (index-of (map :id (:spaces db)) (:id space))]
     (update-in db [:spaces space-idx] merge space))))

;; Remove a space to the list of spaces in the application db
(re-frame/reg-event-db
 ::remove-space
 standard-interceptors
 (fn [db [space]]
   (let [old-spaces (:spaces db)
         new-spaces (into [] (remove #(= (:id %) (:id space)) old-spaces))]
     (assoc db :spaces new-spaces))))

;; Set a space-uuid to the selected space-uuid db key
(re-frame/reg-event-db
 ::select-space
 standard-interceptors
 (fn [db [space]]
   (assoc-in db [:selected :space-id] (str (:id space)))))

;; Set a space-uuid to the selected space-uuid db key based on active spaces and
;; user assigned space
(re-frame/reg-event-db
 ::select-default-space
 standard-interceptors
 (fn [db [space]]
   (let [active-spaces-id (->> (:spaces db)
                               (remove #(false? (:isActive %)))
                               (map :id))
         user-space-id (get-in db [:settings :userinfo :spaceId])
         default-space-id (or user-space-id (first active-spaces-id))]
     (assoc-in db [:selected :space-id] default-space-id))))
